import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { LoadingButton } from "@mui/lab";
import { ButtonProps } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback, useState } from "react";

type ExcelDownloadButtonProps = {
  isLoading?: boolean;
  // Prefer returning Response so we can read Content-Disposition for filename
  getBlob: () => Promise<Response | Blob | File | undefined>;
} & Omit<ButtonProps, "onClick">;

function extractFilenameFromContentDisposition(
  contentDisposition: string | null
): string | undefined {
  if (!contentDisposition) return undefined;

  // Prefer RFC 5987 filename* if present (e.g., filename*=UTF-8''encoded%20name.xlsx)
  const filenameStarMatch = /filename\*=([^;]+)/i.exec(contentDisposition);
  if (filenameStarMatch?.[1]) {
    const value = filenameStarMatch[1].trim();
    // value may be like UTF-8''encoded%20name.xlsx or just encoded
    // Extract portion after last two single quotes per RFC 5987 (charset''value)
    const doubleQuoteStripped = value.replace(/^"|"$/g, "");
    const twoPrimeIndex = doubleQuoteStripped.indexOf("''");
    const encoded =
      twoPrimeIndex >= 0
        ? doubleQuoteStripped.slice(twoPrimeIndex + 2)
        : doubleQuoteStripped;
    try {
      const decoded = decodeURIComponent(encoded);
      if (decoded) return decoded;
    } catch {
      // fall through to basic filename parsing
    }
  }

  // Fallback: filename="name.xlsx" or filename=name.xlsx
  const filenameMatch = /filename=([^;]+)/i.exec(contentDisposition);
  if (filenameMatch?.[1]) {
    const raw = filenameMatch[1].trim();
    return raw.replace(/^"|"$/g, "");
  }
  return undefined;
}

export function ExcelDownloadButton({
  getBlob,
  isLoading: isLoadingProp = false,
  ...buttonProps
}: ExcelDownloadButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTenantTranslation();
  const handleClick = useCallback(async () => {
    setIsLoading(true);
    const data = await getBlob();
    setIsLoading(false);
    if (!data) return;

    let blob: Blob;
    let filename: string | undefined;

    if (data instanceof Response) {
      filename = extractFilenameFromContentDisposition(
        data.headers.get("Content-Disposition")
      );
      blob = await data.blob();
    } else {
      blob = data;
      if (data instanceof File && data.name) {
        filename = data.name;
      }
    }

    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    if (filename) {
      link.setAttribute("download", filename);
    } else {
      link.setAttribute("download", "download.xlsx");
    }
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  }, [getBlob]);

  const loading = isLoading || isLoadingProp;

  return (
    <LoadingButton
      onClick={() => void handleClick()}
      disabled={loading}
      {...buttonProps}
      startIcon={<CloudDownloadIcon />}
      loading={loading}
    >
      {t("downloadExcel")}
    </LoadingButton>
  );
}
